import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import {fuzzyMatchWithMapping} from '@wandb/weave/common/util/fuzzyMatch';
import {isNotNullOrUndefined, Nullable} from '@wandb/weave/common/util/types';
import produce from 'immer';
import _ from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {StrictInputProps} from 'semantic-ui-react';

import iconCheck from '../../assets/ic-check.svg';
import iconCheckGray from '../../assets/ic-check-gray.svg';
import iconLockClosed from '../../assets/ic-lock-closed.svg';
import {
  useFeaturedReportsMetadataQuery,
  useTeamPageProjectsQuery,
  useUserReportsQuery,
} from '../../generated/graphql';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {profileReportShowcased} from '../../services/analytics';
import {
  accessIsPublicOrOpen,
  AccessOptions,
  getProjectAccess,
} from '../../util/permissions';
import {
  ShowcasedReport,
  useEntityPageContext,
  useEntityPageUpdaterContext,
} from './context';
import * as S from './CustomizeShowcaseModal.styles';
import {getShowcasedReport} from './util';

type ShowcaseCandidate = ShowcasedReport & {isPublic: boolean};

type ReportNode = {
  id: string;
  displayName?: Nullable<string>;
  description?: Nullable<string>;
  previewUrl?: Nullable<string>;
  createdAt: string;
  updatedAt?: Nullable<string>;
  viewCount: number;
  project?: Nullable<{
    name: string;
    entityName: string;
    access?: Nullable<string>;
  }>;
};

type CustomizeShowcasedReportsModalUserProps = Omit<
  CustomizeShowcasedReportsModalProps,
  `reports` | `loading`
>;

export const CustomizeShowcasedReportsModalUser: React.FC<CustomizeShowcasedReportsModalUserProps> =
  React.memo(props => {
    const {showcasedReportIDs} = useEntityPageContext();

    const sortedReportIDs = useSortedIDs(showcasedReportIDs);
    const {data, loading: loadingShowcasedReportMetadata} =
      useFeaturedReportsMetadataQuery({
        variables: {ids: sortedReportIDs},
      });

    const showcasedReports: ShowcaseCandidate[] = useMemo(() => {
      const metadataNodes = data?.views?.edges.map(e => e.node) ?? [];
      const metadataByReportID: Map<string, (typeof metadataNodes)[number]> =
        new Map(
          metadataNodes
            .map(n => {
              if (n == null) {
                return null;
              }
              return [n.id, n] as const;
            })
            .filter(isNotNullOrUndefined)
        );

      return showcasedReportIDs
        .map(id => {
          const metadata = metadataByReportID.get(id);
          if (metadata == null) {
            return null;
          }
          return getShowcaseCandidateFromReportNode(metadata);
        })
        .filter(isNotNullOrUndefined);
    }, [showcasedReportIDs, data]);

    const {reports: userReports, loading: loadingUserReports} =
      useShowcaseCandidatesForUser();

    const reports = _.uniqBy([...showcasedReports, ...userReports], `id`);
    const loading = loadingShowcasedReportMetadata || loadingUserReports;

    return (
      <CustomizeShowcasedReportsModal {...props} {...{reports, loading}} />
    );
  });

type CustomizeShowcasedReportsModalTeamProps = Omit<
  CustomizeShowcasedReportsModalProps,
  `reports` | `loading`
>;

export const CustomizeShowcasedReportsModalTeam: React.FC<CustomizeShowcasedReportsModalTeamProps> =
  React.memo(props => {
    const {entityName, showcasedReportIDs} = useEntityPageContext();

    const sortedReportIDs = useSortedIDs(showcasedReportIDs);
    const {data, loading: loadingShowcasedReportMetadata} =
      useFeaturedReportsMetadataQuery({
        variables: {ids: sortedReportIDs},
      });

    const showcasedReports: ShowcaseCandidate[] = useMemo(() => {
      const metadataNodes = data?.views?.edges.map(e => e.node) ?? [];
      const metadataByReportID: Map<string, (typeof metadataNodes)[number]> =
        new Map(
          metadataNodes
            .map(n => {
              if (n == null) {
                return null;
              }
              return [n.id, n] as const;
            })
            .filter(isNotNullOrUndefined)
        );

      return showcasedReportIDs
        .map(id => {
          const metadata = metadataByReportID.get(id);
          if (metadata == null) {
            return null;
          }
          return getShowcaseCandidateFromReportNode(metadata);
        })
        .filter(isNotNullOrUndefined);
    }, [showcasedReportIDs, data]);

    const {reports: userReports, loading: loadingUserReports} =
      useShowcaseCandidatesForUser();
    const {reports: teamReports, loading: loadingTeamReports} =
      useShowcaseCandidatesForTeam(entityName);

    const reports = _.uniqBy(
      [...showcasedReports, ...userReports, ...teamReports],
      'id'
    );
    const loading =
      loadingShowcasedReportMetadata ||
      loadingUserReports ||
      loadingTeamReports;

    return (
      <CustomizeShowcasedReportsModal {...props} {...{reports, loading}} />
    );
  });

type CustomizeShowcasedReportsModalProps = {
  reports: ShowcaseCandidate[];
  loading: boolean;
  onClose: () => void;
};

const CustomizeShowcasedReportsModal: React.FC<CustomizeShowcasedReportsModalProps> =
  React.memo(({reports, loading, onClose}) => {
    const {entityName, showcasedReportIDs} = useEntityPageContext();
    const {setShowcasedReportIDs} = useEntityPageUpdaterContext();

    const showcasedReportIDSet: Set<string> = useMemo(
      () => new Set(showcasedReportIDs),
      [showcasedReportIDs]
    );
    // This is the exact same as above but doesn't update.
    // We use this for sorting the options.
    // The sort shouldn't jump around when new items are selected.
    const showcasedReportIDSetStatic: Set<string> = useMemo(
      () => new Set(showcasedReportIDs),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const showcaseReportID = useCallback(
      (reportID: string) => {
        profileReportShowcased({entityName, reportID});
        setShowcasedReportIDs(prev =>
          produce(prev, draft => {
            draft.push(reportID);
          })
        );
      },
      [entityName, setShowcasedReportIDs]
    );

    const unshowcaseReportID = useCallback(
      (reportID: string) => {
        setShowcasedReportIDs(prev =>
          produce(prev, draft => {
            const i = draft.findIndex(id => id === reportID);
            if (i > -1) {
              draft.splice(i, 1);
            }
          })
        );
      },
      [setShowcasedReportIDs]
    );

    const toggleShowcase = useCallback(
      (reportID: string) => {
        if (showcasedReportIDSet.has(reportID)) {
          unshowcaseReportID(reportID);
        } else {
          showcaseReportID(reportID);
        }
      },
      [showcaseReportID, unshowcaseReportID, showcasedReportIDSet]
    );

    const [filter, setFilter] = useState(``);

    const filteredReports = useMemo(() => {
      if (!filter) {
        return reports;
      }
      return fuzzyMatchWithMapping(reports, filter, r => r.name);
    }, [reports, filter]);

    const sortedReports = useMemo(() => {
      if (filter) {
        // Fuzzy match filtering already applies its own sort
        // which we don't want to override.
        return filteredReports;
      }

      const selectedReports = sortByViewsDescending(
        filteredReports.filter(r => showcasedReportIDSetStatic.has(r.id))
      );

      const availableReports = sortByViewsDescending(
        filteredReports.filter(
          r => !showcasedReportIDSetStatic.has(r.id) && r.isPublic
        )
      );

      const disabledReports = sortByViewsDescending(
        filteredReports.filter(
          r => !showcasedReportIDSetStatic.has(r.id) && !r.isPublic
        )
      );

      return [...selectedReports, ...availableReports, ...disabledReports];
    }, [filter, filteredReports, showcasedReportIDSetStatic]);

    return (
      <S.Modal open onClose={onClose}>
        <S.Header>Customize your featured reports</S.Header>
        <S.Subheader>
          Select those reports you'd like others to see when they view your
          profile.
        </S.Subheader>
        <S.LoadedContent>
          {loading ? (
            <WandbLoader name="customize-showcased-reports-modal-loader" />
          ) : (
            <>
              <S.FilterInput
                placeholder="Filter reports"
                icon={<S.SearchIcon />}
                value={filter}
                onChange={
                  ((e, {value}) =>
                    setFilter(value)) as StrictInputProps[`onChange`]
                }
              />
              <S.Options>
                {sortedReports.map(r => {
                  const option = (
                    <S.Option
                      key={r.id}
                      selected={showcasedReportIDSet.has(r.id)}
                      disabled={!r.isPublic}
                      onClick={() => {
                        if (r.isPublic) {
                          toggleShowcase(r.id);
                        }
                      }}>
                      <S.Name>{r.name}</S.Name>
                      <S.Details>{r.viewCount} views</S.Details>
                      <S.Icon
                        src={
                          showcasedReportIDSet.has(r.id)
                            ? iconCheck
                            : r.isPublic
                            ? iconCheckGray
                            : iconLockClosed
                        }
                      />
                    </S.Option>
                  );
                  return r.isPublic ? (
                    option
                  ) : (
                    <S.DisabledPopup
                      key={r.id}
                      basic
                      inverted
                      position="top center"
                      popperModifiers={{
                        preventOverflow: {
                          boundariesElement: 'viewport',
                        },
                      }}
                      trigger={option}
                      content={
                        <S.DisabledPopupContent>
                          <S.DisabledPopupIcon />
                          This report is inside a private project
                        </S.DisabledPopupContent>
                      }
                    />
                  );
                })}
              </S.Options>
              <S.CloseButton onClick={onClose}>Done</S.CloseButton>
            </>
          )}
        </S.LoadedContent>
      </S.Modal>
    );
  });

function useShowcaseCandidatesForUser(): {
  reports: ShowcaseCandidate[];
  loading: boolean;
} {
  const {data, loading} = useUserReportsQuery();

  const reports = useMemo(() => {
    const reportNodes =
      data?.viewer?.views?.edges
        .map(e => e.node)
        .filter(isNotNullOrUndefined) ?? [];

    return reportNodes
      .map(getShowcaseCandidateFromReportNode)
      .filter(isNotNullOrUndefined);
  }, [data]);

  return {reports, loading};
}

function useShowcaseCandidatesForTeam(entityName: string): {
  reports: ShowcaseCandidate[];
  loading: boolean;
} {
  const {data, loading} = useTeamPageProjectsQuery({
    variables: {entityName},
  });

  const reports = useMemo(() => {
    const projectNodes =
      data?.entity?.projects?.edges
        .map(e => e.node)
        .filter(isNotNullOrUndefined) ?? [];

    const reportNodes = projectNodes
      .map(
        projNode =>
          projNode.allViews?.edges
            .map(e => e.node)
            .filter(isNotNullOrUndefined) ?? []
      )
      .flat();

    return reportNodes
      .map(getShowcaseCandidateFromReportNode)
      .filter(isNotNullOrUndefined);
  }, [data]);

  return {reports, loading};
}

function getShowcaseCandidateFromReportNode(
  r: ReportNode
): ShowcaseCandidate | null {
  if (
    (r.project != null &&
      (r.project.access == null ||
        !getProjectAccess(r.project.access as AccessOptions))) ||
    !r.displayName
  ) {
    return null;
  }

  const showcasedReport = getShowcasedReport(r);
  if (showcasedReport == null) {
    return null;
  }

  return {
    ...showcasedReport,
    isPublic:
      r.project == null ||
      (r.project.access != null &&
        accessIsPublicOrOpen(r.project.access as AccessOptions)),
  };
}

function sortByViewsDescending(
  items: ShowcaseCandidate[]
): ShowcaseCandidate[] {
  return _.sortBy(items, i => -i.viewCount);
}

// HAX: We don't want the query to refetch when the order of the IDs changes.
// This function only returns a new object when the set of IDs changes.
function useSortedIDs(ids: string[]): string[] {
  const sortedIDString = useMemo(() => _.sortBy(ids).join(','), [ids]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => _.sortBy(ids), [sortedIDString]);
}
